import { Unstable_Grid2 as Grid, Stack } from '@mui/material';
import { useMemo } from 'react';

import {
  Box,
  Card,
  CardContent,
  Divider,
  InfoField,
  Typography,
  TableCell,
  CircularProgress,
} from '@occc-app/design-system';
import { LoadedData } from '@occc/utils';
import {
  CustomerScanDataOverviewFragment,
  Cpg,
  ScanDataSubmissionHealthMetricsFragment,
  ScanDataSubmittedTransactionsStoreLevelDetailsFragment,
  ScanDataMissingStoreStoreLevelDetailsFragment,
  ScanDataReimbursementsStoreLevelDetailsFragment,
  ScanDataReportedTransactionsStoreLevelDetailsFragment,
  TimeSeriesDataPoint,
  ScanDataMetricsTimeSeriesFragment,
} from '@occc/graphql';
import { DateTimeFormat, formatDateTime, parseDate } from '@occc-app/lib/date';
import { MetricTabs, MetricTabsProps } from '@occc/ui-design-system';
import { separateEveryThousandByComma } from '@occc-app/lib/numbers';
import { Feature, useIsUserPermittedTo } from '@occc/utils-access-control';
import { formatMoneyAmount } from '@occc-app/lib/currency';

import { ScanDataEstimatedReimbursementsChart } from './scan-data-estimated-reimbursements-chart/scan-data-estimated-reimbursements-chart.component';
import { ScanDataStoreDetailsTable } from './scan-data-store-details-table';
import { ScanDataReportLinesChart } from './scan-data-report-lines-chart';
import { ScanDataMissingStoresChart } from './scan-data-missing-stores-chart';
import { ScanDataMissingStoresInfo } from './scan-data-missing-stores-info';
import { createStatusByCpgMap } from './scan-data-overview.utils';
import { ScanDataWeeklyReimbursementsTable } from './scan-data-weekly-reimbursements-table';
import { ScanDataMetric } from './scan-data-overview.types';
import { ScanDataMetricBarChart } from './scan-data-metric-bar-chart/scan-data-metric-bar-chart.component';
import { SHOW_CENTS_THRESHOLD } from './scan-data-overview.constants';

import { LoadedDataWithPagination } from '../../scan-data.types';

type ScanDataOverviewProps = {
  customerScanDataOverviewLoadedData: LoadedData<CustomerScanDataOverviewFragment>;
  submissionHealthMetricsLoadedData: LoadedData<ScanDataSubmissionHealthMetricsFragment>;
  metricsData: ScanDataMetric[];
  submittedTransactionsTableData: LoadedDataWithPagination<
    ScanDataSubmittedTransactionsStoreLevelDetailsFragment[]
  >;
  missingStoresTableData: LoadedDataWithPagination<ScanDataMissingStoreStoreLevelDetailsFragment[]>;
  reimbursementsTableData: LoadedDataWithPagination<
    ScanDataReimbursementsStoreLevelDetailsFragment[]
  >;
  reportedTransactionsTableData: LoadedDataWithPagination<
    ScanDataReportedTransactionsStoreLevelDetailsFragment[]
  >;
  handleMetricChange: MetricTabsProps['onChange'];
  selectedMetric: ScanDataMetric;
  onDownloadCSV: () => void;
  isDownloading: boolean;
  searchValue: string;
  onSearchChange: (searchValue: string) => void;
  scanDataMetricsTimeSeriesLoadedData: LoadedData<ScanDataMetricsTimeSeriesFragment>;
  selectedMetricChartData: TimeSeriesDataPoint[] | undefined;
};

export const ScanDataOverview = ({
  handleMetricChange,
  selectedMetric,
  onDownloadCSV,
  isDownloading,
  searchValue,
  onSearchChange,
  customerScanDataOverviewLoadedData,
  submissionHealthMetricsLoadedData,
  metricsData,
  submittedTransactionsTableData,
  missingStoresTableData,
  reimbursementsTableData,
  reportedTransactionsTableData,
  scanDataMetricsTimeSeriesLoadedData,
  selectedMetricChartData,
}: ScanDataOverviewProps) => {
  const isDownloadAllowed = useIsUserPermittedTo(Feature.SCAN_DATA_DOWNLOAD_CSV);

  const weeklyReimbursementsTrends = useMemo(() => {
    return submissionHealthMetricsLoadedData.data?.weeklyReimbursementsTrends?.slice().reverse();
  }, [submissionHealthMetricsLoadedData.data?.weeklyReimbursementsTrends]);

  const { loading: isOverviewDataLoading } = customerScanDataOverviewLoadedData;

  const statusByCpg = useMemo(
    () => createStatusByCpgMap(customerScanDataOverviewLoadedData.data?.cpgSubmissionStatus),
    [customerScanDataOverviewLoadedData],
  );

  const renderCpgStatus = (cpg: Cpg) => {
    if (isOverviewDataLoading && !customerScanDataOverviewLoadedData.data) {
      return <CircularProgress size={20} />;
    }

    if (customerScanDataOverviewLoadedData.error || !customerScanDataOverviewLoadedData.data) {
      return null;
    }

    return statusByCpg[cpg] ? 'Active' : 'Inactive';
  };

  const renderLastSubmissionDate = () => {
    const lastSubmissionDate = customerScanDataOverviewLoadedData.data?.lastSubmissionDate;

    if (isOverviewDataLoading && !lastSubmissionDate) {
      return <CircularProgress size={20} />;
    }

    if (!lastSubmissionDate) {
      return null;
    }

    const date = parseDate(lastSubmissionDate);
    return formatDateTime(date, DateTimeFormat.DateSlashed);
  };

  const renderMetricTable = () => {
    const baseProps = {
      onDownloadCSV,
      isDownloading,
      searchValue,
      onSearchChange,
      isDownloadAllowed,
    };

    if (selectedMetric.metricType === 'stores-submitted') {
      return (
        <ScanDataStoreDetailsTable
          {...baseProps}
          title={'Stores Submitted'}
          data={submittedTransactionsTableData.data}
          loading={submittedTransactionsTableData.loading}
          error={submittedTransactionsTableData.error}
          paginationProps={submittedTransactionsTableData.paginationProps}
        />
      );
    } else if (selectedMetric.metricType === 'missing-stores') {
      return (
        <ScanDataStoreDetailsTable
          {...baseProps}
          title={selectedMetric.label}
          data={missingStoresTableData.data}
          loading={missingStoresTableData.loading}
          error={missingStoresTableData.error}
          paginationProps={missingStoresTableData.paginationProps}
        />
      );
    } else if (selectedMetric.metricType === 'transactions-reported') {
      return (
        <ScanDataStoreDetailsTable
          {...baseProps}
          title={selectedMetric.label}
          data={reportedTransactionsTableData.data}
          loading={reportedTransactionsTableData.loading}
          error={reportedTransactionsTableData.error}
          paginationProps={reportedTransactionsTableData.paginationProps}
          additionalHeaderCells={<TableCell>Transactions Reported</TableCell>}
          renderAdditionalRowCells={(item) => (
            <TableCell align='right'>
              {separateEveryThousandByComma(item.reportedTransactions)}
            </TableCell>
          )}
        />
      );
    } else if (selectedMetric.metricType === 'reimbursements') {
      return (
        <ScanDataStoreDetailsTable
          {...baseProps}
          title={selectedMetric.label}
          data={reimbursementsTableData.data}
          loading={reimbursementsTableData.loading}
          error={reimbursementsTableData.error}
          paginationProps={reimbursementsTableData.paginationProps}
          additionalHeaderCells={
            <TableCell key='reimbursements'>Estimated Reimbursement (per store)</TableCell>
          }
          renderAdditionalRowCells={({ estimatedReimbursements }) => (
            <TableCell align='right'>
              {formatMoneyAmount(
                estimatedReimbursements.loyaltyTotal + estimatedReimbursements.multipackTotal,
              )}
            </TableCell>
          )}
        />
      );
    }

    return null;
  };

  const selectedMetricIndex = useMemo(
    () =>
      Math.max(
        0,
        metricsData.findIndex((metric) => metric.metricType === selectedMetric.metricType),
      ),
    [selectedMetric, metricsData],
  );

  return (
    <Stack>
      <Card sx={{ mb: 1 }}>
        <CardContent>
          <Typography variant='subtitle1' mb={2}>
            Overview
          </Typography>

          <Grid container spacing={2}>
            <Grid xs={6} sm={3}>
              <InfoField label='Altria Scan Data Status' value={renderCpgStatus(Cpg.Altria)} />
            </Grid>

            <Grid xs={6} sm={3}>
              <InfoField label='RJR Scan Data Status' value={renderCpgStatus(Cpg.Circana)} />
            </Grid>

            <Grid xs={6} sm={3}>
              <InfoField label='MSA Scan Data Status' value={renderCpgStatus(Cpg.Msa)} />
            </Grid>

            <Grid xs={6} sm={3}>
              <InfoField label='Last Submission Date' value={renderLastSubmissionDate()} />
            </Grid>
          </Grid>

          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid xs={12} sm={6}>
              <Typography variant='subtitle1' mb={3}>
                Estimated Reimbursements
              </Typography>

              <ScanDataEstimatedReimbursementsChart
                loading={submissionHealthMetricsLoadedData.loading}
                error={submissionHealthMetricsLoadedData.error}
                data={submissionHealthMetricsLoadedData.data?.estimatedReimbursements}
              />
            </Grid>

            <Grid xs={12} sm={6}>
              <Typography variant='subtitle1' mb={3}>
                Weekly Estimated Reimbursements
              </Typography>

              <ScanDataWeeklyReimbursementsTable
                loading={submissionHealthMetricsLoadedData.loading}
                error={submissionHealthMetricsLoadedData.error}
                data={weeklyReimbursementsTrends}
              />
            </Grid>
          </Grid>

          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid xs={12} sm={6}>
              <Box display='flex' flexDirection='row' mb={3}>
                <Typography variant='subtitle1'>Missing Stores</Typography>
                <ScanDataMissingStoresInfo />
              </Box>

              {/* Note: totalStores from API actually represents submitted stores count.
                  Actual total stores = submitted stores + missing stores */}
              <ScanDataMissingStoresChart
                loading={submissionHealthMetricsLoadedData.loading}
                error={submissionHealthMetricsLoadedData.error}
                data={submissionHealthMetricsLoadedData.data?.missingStores}
                submittedStores={submissionHealthMetricsLoadedData.data?.totalStores}
              />
            </Grid>

            <Grid xs={12} sm={6}>
              <Typography variant='subtitle1' mb={3}>
                Report Lines
              </Typography>

              <ScanDataReportLinesChart
                loading={submissionHealthMetricsLoadedData.loading}
                error={submissionHealthMetricsLoadedData.error}
                data={submissionHealthMetricsLoadedData.data?.reportLines}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent
          sx={{
            p: 0,
            // Use a more specific selector to override the CardContent default padding
            '&&': {
              paddingBottom: 0,
            },
          }}
        >
          <MetricTabs
            index={selectedMetricIndex}
            items={metricsData}
            onChange={handleMetricChange}
          />

          <Box height={200} py={3} px={2}>
            <ScanDataMetricBarChart
              loading={scanDataMetricsTimeSeriesLoadedData.loading}
              error={scanDataMetricsTimeSeriesLoadedData.error}
              data={selectedMetricChartData}
              metricsType={selectedMetric.metricType === 'reimbursements' ? 'currency' : 'number'}
              centsThreshold={
                selectedMetric.metricType === 'reimbursements' ? SHOW_CENTS_THRESHOLD : undefined
              }
            />
          </Box>

          <Divider />

          {selectedMetric && renderMetricTable()}
        </CardContent>
      </Card>
    </Stack>
  );
};
